import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { ROLE_LABELS, useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, ShieldX } from "lucide-react";
import { Modal, TextField, SelectField } from "./SponsorsPage";

const ROLES = ["super_admin", "event_admin", "sponsorship_team", "volunteer_coordinator", "view_only"];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/users", { withCredentials: true });
    setUsers(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!editing.email) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/users", editing, { withCredentials: true });
      toast.success("User invited");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const updateRole = async (u, role) => {
    await api.patch(`/admin/users/${u.user_id}`, { role }, { withCredentials: true });
    toast.success(`${u.email} is now ${ROLE_LABELS[role]}`);
    await load();
  };

  const setStatus = async (u, status) => {
    await api.patch(`/admin/users/${u.user_id}`, { status }, { withCredentials: true });
    toast.success(`${u.email} ${status}`);
    await load();
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? They will lose all access immediately.`)) return;
    await api.delete(`/admin/users/${u.user_id}`, { withCredentials: true });
    toast.success("Deleted");
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">USER MANAGEMENT</p>
          <h1 className="font-display text-4xl text-white">Team & Roles</h1>
          <p className="text-white/50 mt-2 text-sm">Only Super Admins can see and modify this page.</p>
        </div>
        <button data-testid="invite-user-btn" onClick={() => setEditing({ email: "", name: "", role: "event_admin" })} className="btn-pink">
          <Plus size={16} /> INVITE USER
        </button>
      </div>

      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
            <tr><th className="px-4 py-3">Email</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white">{u.email}{u.user_id === me?.user_id && <span className="ml-2 text-xs text-aawta-pink">(you)</span>}</td>
                <td className="px-4 py-3 text-white/70">{u.name || "—"}</td>
                <td className="px-4 py-3">
                  <select value={u.role || ""} disabled={u.user_id === me?.user_id} onChange={(e) => updateRole(u, e.target.value)}
                    className="bg-aawta-jet/60 border border-white/10 text-white text-sm px-2 py-1 disabled:opacity-50">
                    <option value="" disabled>— Pending —</option>
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className={u.status === "active" ? "text-green-400" : u.status === "pending" ? "text-yellow-300" : "text-white/40"}>
                    {(u.status || "pending").toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {u.user_id !== me?.user_id && (
                    <>
                      {u.status === "active" ? (
                        <button onClick={() => setStatus(u, "disabled")} title="Disable" className="text-white/60 hover:text-yellow-300 mr-3"><ShieldX size={14} /></button>
                      ) : (
                        <button onClick={() => setStatus(u, "active")} title="Activate" className="text-white/60 hover:text-green-400 mr-3"><ShieldCheck size={14} /></button>
                      )}
                      <button onClick={() => remove(u)} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title="Invite User" onClose={() => setEditing(null)}>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Email *" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testId="invite-email" />
            <TextField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="invite-name" />
            <SelectField label="Role" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} options={ROLES} testId="invite-role" />
          </div>
          <p className="text-xs text-white/40 mt-4">User will receive access the next time they sign in via Google with this email.</p>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditing(null)} className="btn-ghost py-2.5">CANCEL</button>
            <button data-testid="save-invite-btn" onClick={invite} disabled={saving} className="btn-pink disabled:opacity-50">
              {saving ? "INVITING…" : "INVITE"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
